import React, { useContext, useEffect, useState } from 'react';
import { ApLoaderDefault, ApLoaderText } from '@alixpartners/ui-components';
import SecondaryNav from './SecondaryNav';
import {
	getDateRangeMonthYearSrc,
	getDateRangeMonthYearState,
	getMinMaxDatesFromData,
	getTitle,
} from './utils';
import { QueryStringContext } from 'contexts/QueryStringContext';
import { SettingsOptionsContext } from 'contexts/SettingsOptionsContext';
import { SettingsContext } from 'contexts/SettingsContext';
import { NoData } from './NoData';
import {
	Wrapper,
	Main,
	Title,
	ChartWrapper,
	LoaderWrapper,
} from './Benchmarks.styles';
import { getAliasNameData, getCompanyMetadataOptions } from 'utils/apiRequests';
import BenchmarkContainer from './components/BenchmarkContainer/BenchmarkContainer';
import { getLocalStorageItem } from 'utils/localStorage';
import {
	DENOMINATION_UNITS,
	UNIT_MAP,
	getIdealDenomination,
} from 'utils/number';

const LOCAL_STORAGE_KEY = 'BENCHMARKS-CHART-OPTIONS';

const getDefaultFilterOptions = () => {
	const localStorageState = getLocalStorageItem(LOCAL_STORAGE_KEY);
	const defaultState = {
		spendRange: [0, 0],
		minMaxSpendRange: [0, 0],
		spendRangeUnitMax: '',
		spendRangeUnitMin: '',
	};

	return {
		...defaultState,
		...localStorageState,
	};
};

const Benchmarks = () => {
	const [companies, setCompanies] = useState([]);
	const [industryOptions, setIndustryOptions] = useState([]);
	const [isMetadataLoading, setIsMetadataLoading] = useState(true);
	const [initialFilters, setInitialFilters] = useState(
		getDefaultFilterOptions()
	);
	const { dateRangeId, dateFrom, dateTo } = useContext(QueryStringContext);
	const { options } = useContext(SettingsOptionsContext);
	const { dateRanges } = options;
	const { minDate, maxDate } = getMinMaxDatesFromData(dateRanges);
	const wholeRange = getDateRangeMonthYearSrc(minDate, maxDate);
	const selectedRange = getDateRangeMonthYearState(dateFrom, dateTo);
	const {
		companiesMetadata: { companies: companiesMetadata },
	} = useContext(SettingsContext);

	useEffect(
		() => {
			const companiesList = companiesMetadata.map(x => {
				return { name: x.companyName };
			});
			setCompanies(companiesList);

			async function fetchAliasAndMetadata() {
				const aliasResponse = await getAliasNameData();
				if (aliasResponse.succeeded) {
					if (aliasResponse.data.metaDataOutputs !== null) {
						let centralIndustry = [];
						let filteredCentralIndustry = [];
						const aliasIndustry = [
							...new Set(
								aliasResponse.data.metaDataOutputs.map(el => el.industry)
							),
						];

						const companyResponse = await getCompanyMetadataOptions();
						if (!!companyResponse?.industries) {
							if (companyResponse.industries.data.length > 0) {
								centralIndustry = companyResponse.industries.data;
							}
						}
						if (centralIndustry.length > 0) {
							filteredCentralIndustry = centralIndustry.filter(item =>
								aliasIndustry.includes(item.key)
							);
						}
						const industryList = filteredCentralIndustry.map(el => {
							return { label: el.value, value: el.key };
						});
						setIndustryOptions(industryList);

						const revenueList = [
							...new Set(
								aliasResponse.data.metaDataOutputs.map(el => el.revenue)
							),
						].map(x => x);
						const lowestRevenue = Math.min(...revenueList);
						const highestRevenue = Math.max(...revenueList);
						const denominationMin = getIdealDenomination(lowestRevenue);
						const denominationMax = getIdealDenomination(highestRevenue);
						const stdDenomination = UNIT_MAP.m;
						const formattedMinValue = Math.ceil(
							lowestRevenue / stdDenomination
						);
						const formattedMaxValue = Math.ceil(
							highestRevenue / stdDenomination
						);
						setInitialFilters(prevState => ({
							...prevState,
							spendRange: [formattedMinValue, formattedMaxValue], // settings default spend range
							minMaxSpendRange: [formattedMinValue, formattedMaxValue],
							spendRangeUnitMin: DENOMINATION_UNITS[denominationMin],
							spendRangeUnitMax: DENOMINATION_UNITS[denominationMax],
							isSet: true,
						}));
					}
				}
			}
			fetchAliasAndMetadata();
			setIsMetadataLoading(false);
		},
		[companiesMetadata]
	);

	const dateRangesOptions = options ? options.dateRanges : [];
	const selectedDateRange = dateRangesOptions.find(
		item => item.id.toString() === dateRangeId
	);

	if (!companies.length) {
		const title = getTitle(selectedDateRange);
		return (
			<Wrapper>
				<SecondaryNav
					companies={[
						{
							label: 'NO COMPANIES',
							value: null,
							disabled: true,
						},
					]}
					disabledCompany
				/>
				<ChartWrapper>
					<Title>{title}</Title>
				</ChartWrapper>
				<NoData message="There are no companies with indirect spend in the data" />
			</Wrapper>
		);
	}

	return (
		<>
			{isMetadataLoading ? (
				<Wrapper>
					<SecondaryNav companies={companies.map(c => c.name)} />
					<Main>
						<ChartWrapper >
							<Title>Chart Title</Title>
							<LoaderWrapper>
								<ApLoaderDefault loaderText={() => <ApLoaderText />} />
							</LoaderWrapper>
						</ChartWrapper>
					</Main>
				</Wrapper>
			) : (
				<> 
					{initialFilters.isSet > 0 && (
						<BenchmarkContainer
							companies={companies}
							industryOptions={industryOptions}
							initialFilters={initialFilters}
							dateRangeData={{ wholeRange, selectedRange }}
						/>
					)}
				</>
			)}
		</>
	);
};

export default Benchmarks;
